import React from "react";
import { useNavigate } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();

  return (
    <div>
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-brand-800 via-brand-900 to-brand-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
              Administración de Terrenos
            </h1>
            <p className="text-xl text-blue-200 mb-2">Gobierno de Puerto Rico</p>
            <p className="text-blue-300 mb-10">
              Sistema de consulta de parcelas, contratos y arrendamientos de terrenos públicos
            </p>

            {/* Quick Search Cards */}
            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <button
                onClick={() => navigate(isAuthenticated ? "/parcelas" : "/arrendamiento")}
                className="group bg-white/10 backdrop-blur border border-white/10 rounded-2xl p-6 text-left hover:bg-white/15 transition"
              >
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-1">Buscar Parcelas</h3>
                <p className="text-sm text-blue-200">
                  {isAuthenticated
                    ? "Localice terrenos públicos por municipio, ID de parcela o ubicación en el mapa"
                    : "Inicie sesión para buscar y gestionar parcelas"
                  }
                </p>
              </button>

              <button
                onClick={() => navigate(isAuthenticated ? "/contratos" : "/arrendamiento")}
                className="group bg-white/10 backdrop-blur border border-white/10 rounded-2xl p-6 text-left hover:bg-white/15 transition"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-1">Buscar Contratos</h3>
                <p className="text-sm text-blue-200">
                  {isAuthenticated
                    ? "Consulte contratos de arrendamiento por cliente, municipio o número"
                    : "Inicie sesión para acceder a contratos de arrendamiento"
                  }
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Parcelas", value: "1,200+", color: "bg-green-50 text-green-700 border-green-100" },
            { label: "Contratos Activos", value: "340+", color: "bg-blue-50 text-blue-700 border-blue-100" },
            { label: "Municipios", value: "78", color: "bg-purple-50 text-purple-700 border-purple-100" },
            { label: "Arrendamientos", value: "260+", color: "bg-amber-50 text-amber-700 border-amber-100" },
          ].map((stat) => (
            <div key={stat.label} className={`${stat.color} border rounded-xl p-4 text-center shadow-sm`}>
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="text-sm opacity-80">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Mapa Interactivo</h3>
            <p className="text-sm text-gray-500">Visualice todas las parcelas públicas de Puerto Rico en un mapa interactivo con filtros por municipio y disponibilidad.</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Búsqueda Avanzada</h3>
            <p className="text-sm text-gray-500">Busque parcelas por ID, municipio, nombre de finca, o número catastral. Contratos por cliente o número.</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Acceso Seguro</h3>
            <p className="text-sm text-gray-500">Información pública disponible para todos.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
